"use client";

import React from "react";
import { ArrowRight, BriefcaseBusiness, ClipboardCheck, FileCheck, Handshake } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const supportAreas = [{
  title: "Governance & Advisory Support",
  description: "We work alongside leadership teams to strengthen governance, improve oversight, support decision-making, and help organizations navigate complex operational and financial challenges with practical, respectful guidance.",
  icon: Handshake
}, {
  title: "Financial Management & Controllership",
  description: "RiverBend provides high-level financial management support where senior finance capacity is limited or evolving. We help strengthen reporting, budgeting, internal controls, and day-to-day financial oversight in a practical and sustainable way.",
  icon: BriefcaseBusiness
}, {
  title: "Audit Catch-Up & Financial Recovery",
  description: "When audits, reporting, or financial systems have fallen behind, we help restore order and momentum. Our work focuses on rebuilding files, addressing historical gaps, and creating a clear path toward stronger audit readiness and long-term stability.",
  icon: FileCheck
}, {
  title: "Project Support, Reporting Systems & Practical Tools",
  description: "We support project planning, delivery, reporting systems, and management tools that help organizations work more clearly and efficiently. Our approach emphasizes simple, usable systems that fit real-world conditions and improve access to timely information.",
  icon: ClipboardCheck
}];
export default function HowWeSupportClients() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="how-we-support-clients" ref={ref} className="bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className={cn("grid grid-cols-1 lg:grid-cols-3 gap-8 xl:gap-12 items-stretch transition-all duration-700 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          <div className="lg:col-span-1 min-w-0">
            <div className="h-full rounded-2xl bg-primary-500 text-primary-foreground p-8 md:p-10 relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(53,128,121,0.26),transparent_42%)] pointer-events-none" />
              <div className="absolute -right-16 -bottom-16 text-[12rem] font-default font-bold text-white/[0.04] leading-none select-none pointer-events-none">
                04
              </div>

              <div className="relative z-10">
                <p className="text-secondary-100 font-default font-bold tracking-widest uppercase text-sm mb-4">
                  How We Support Clients
                </p>

                <h2 className="text-3xl md:text-4xl font-default font-bold text-white mb-6 leading-tight">
                  Clear support for complex financial and organizational work.
                </h2>

                <p className="text-primary-foreground/85 font-default leading-relaxed mb-8">
                  RiverBend provides practical support across governance,
                  financial management, audit recovery, project work, and
                  reporting systems.
                </p>

                <p className="text-primary-foreground/85 font-default leading-relaxed">
                  We help organizations build stronger structures, clearer
                  information, and more dependable momentum in ways that fit
                  their real-world operating conditions.
                </p>

                <div className="mt-8">
                  <Button asChild size="lg" variant="secondary" className="h-12 px-8 rounded-md font-default">
                    <Link to="/services">
                      Explore Our Services
                      <ArrowRight className="ml-2 size-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6 xl:gap-8 min-w-0">
            {supportAreas.map((area, index) => {
            const Icon = area.icon;
            return <div key={area.title} data-index={index} className={cn("h-full transition-all duration-700", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")} style={{
              transitionDelay: `${index * 100}ms`
            }}>
                  <Card className="h-full bg-card border border-border shadow-sm hover:shadow-md hover:border-secondary/40 transition-all duration-300 rounded-lg overflow-hidden">
                    <CardContent className="p-7 md:p-8 flex flex-col h-full">
                      <div className="flex items-center justify-between gap-4 mb-6">
                        <div className="size-12 rounded-md bg-secondary/10 flex items-center justify-center shrink-0">
                          <Icon className="size-6 text-secondary" strokeWidth={1.7} />
                        </div>

                        <span className="text-secondary/70 font-default font-bold text-sm tracking-widest">
                          0{index + 1}
                        </span>
                      </div>

                      <h3 className="text-xl md:text-2xl font-default font-bold text-primary-500 mb-4 leading-snug">
                        {area.title}
                      </h3>

                      <p className="text-muted-foreground font-default leading-relaxed">
                        {area.description}
                      </p>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}